// src/services/songEditService.ts
import apiClient from '../utils/apiClient';
import { SongFormData } from '../components/songEdit/types/songEdit';
import { SongApiPayload } from './types/songApi';

function ensureValidUrl(url: string): string {
  if (!url) return url;
  url = url.trim();
  
  if (!/^https?:\/\//i.test(url)) {
    url = 'https://' + url;
  }
  
  try {
    new URL(url);
    return url;
  } catch (e) {
    return '';
  }
}

export const songEditService = {
  searchSongs: async (query: string): Promise<any> => {
    try {
      const response = await apiClient.get<any>('/song-selections/search', {
        params: { term: query }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching songs:', error);
      throw error;
    }
  },

  getSongById: async (songId: number): Promise<any> => {
    try {
      console.log('songEditService - Getting song details for ID:', songId);
      const response = await apiClient.get(`/songs/${songId}`);
      console.log('songEditService - Response received:', response.data);
      return response.data;
    } catch (error) {
      console.error('songEditService - Error getting song details:', error);
      throw error;
    }
  },

// Fixed saveSong function preserving lyricist

saveSong: async (formData: SongFormData, originalData?: SongFormData) => {
  const isUpdate = !!formData.song_id;
  console.log('[songEditService] Preparing to save song:', {
    isUpdate,
    songId: formData.song_id,
    composer: formData.composerData?.full_name,
    lyricist: formData.lyricistData?.full_name,
    hasOriginalData: !!originalData,
    originalComposer: originalData?.composerData?.full_name,
    originalLyricist: originalData?.lyricistData?.full_name
  });

  // Start building the API payload with only the essential fields
  const apiPayload: Partial<SongApiPayload> = {
    title: formData.title,
    tags: formData.tags || []
  };

  // Add song_id for updates
  if (isUpdate) {
    apiPayload.song_id = formData.song_id;
  }

  // Include video_link only if it has a value
  if (formData.video_link && formData.video_link.trim()) {
    apiPayload.video_link = ensureValidUrl(formData.video_link);
  }
  
  // Include copyright if it has a value
  if (formData.copyright && formData.copyright.trim()) {
    apiPayload.copyright = formData.copyright;
  }

  // Include language (default to 'en' if not set)
  apiPayload.language = formData.language || 'en';

  // For composer, check if we need to update or just use existing
  if (formData.composerData) {
    if (formData.composerData.composer_id) {
      // Check if composer data has changed from original
      const hasComposerChanges = originalData?.composerData ? (
        formData.composerData.full_name !== originalData.composerData.full_name ||
        (formData.composerData.era || '') !== (originalData.composerData.era || '') ||
        (formData.composerData.origin || '') !== (originalData.composerData.origin || '')
      ) : !isUpdate; // If no original data, only assume changes for new songs
      
      console.log('[songEditService] Composer change detection:', {
        hasComposerChanges,
        formEra: formData.composerData.era,
        originalEra: originalData?.composerData?.era,
        formOrigin: formData.composerData.origin,
        originalOrigin: originalData?.composerData?.origin
      });
      
      if (hasComposerChanges) {
        // Update existing composer
        apiPayload.composer_id = Number(formData.composerData.composer_id);
        apiPayload.composer = {
          full_name: formData.composerData.full_name
        };
        if (formData.composerData.era) apiPayload.composer.era = formData.composerData.era;
        if (formData.composerData.origin) apiPayload.composer.origin = formData.composerData.origin;
      } else {
        // Use existing composer without changes
        apiPayload.composer_id = Number(formData.composerData.composer_id);
      }
    } else {
      // Create new composer
      apiPayload.composer = {
        full_name: formData.composerData.full_name
      };
      if (formData.composerData.era) apiPayload.composer.era = formData.composerData.era;
      if (formData.composerData.origin) apiPayload.composer.origin = formData.composerData.origin;
    }
  }
  
  // For lyricist, check if we need to update or just use existing
  console.log('[songEditService] Checking lyricist data:', { 
    hasLyricistData: !!formData.lyricistData,
    lyricistData: formData.lyricistData 
  });
  if (formData.lyricistData) {
    if (formData.lyricistData.composer_id) {
      // Check if lyricist data has changed from original
      const hasLyricistChanges = originalData?.lyricistData ? (
        formData.lyricistData.full_name !== originalData.lyricistData.full_name ||
        (formData.lyricistData.era || '') !== (originalData.lyricistData.era || '') ||
        (formData.lyricistData.origin || '') !== (originalData.lyricistData.origin || '')
      ) : !isUpdate; // If no original data, only assume changes for new songs
      
      console.log('[songEditService] Lyricist change detection:', {
        hasLyricistChanges,
        formEra: formData.lyricistData.era,
        originalEra: originalData?.lyricistData?.era,
        formName: formData.lyricistData.full_name,
        originalName: originalData?.lyricistData?.full_name
      });
      
      console.log('[songEditService] Lyricist changes detected:', hasLyricistChanges);
      
      if (hasLyricistChanges) {
        // Update existing lyricist
        apiPayload.lyricist_id = Number(formData.lyricistData.composer_id);
        apiPayload.lyricist = {
          full_name: formData.lyricistData.full_name
        };
        if (formData.lyricistData.era) apiPayload.lyricist.era = formData.lyricistData.era;
        if (formData.lyricistData.origin) apiPayload.lyricist.origin = formData.lyricistData.origin;
      } else {
        // Use existing lyricist without changes
        apiPayload.lyricist_id = Number(formData.lyricistData.composer_id);
      }
    } else {
      // Create new lyricist
      apiPayload.lyricist = {
        full_name: formData.lyricistData.full_name
      };
      if (formData.lyricistData.era) apiPayload.lyricist.era = formData.lyricistData.era;
      if (formData.lyricistData.origin) apiPayload.lyricist.origin = formData.lyricistData.origin;
    }
  }

  // Handle tune
  if (formData.tuneData) {
    if (formData.tuneData.tune_id) {
      // Use existing tune
      apiPayload.tune_id = Number(formData.tuneData.tune_id);
    } else if (formData.tuneData.tune_name) {
      // Create new tune - send tune details
      apiPayload.tune = {
        tune_name: formData.tuneData.tune_name,
        ...(formData.tuneData.meter_id && { meter_id: formData.tuneData.meter_id }),
        ...(formData.tuneData.is_preferred !== undefined && { is_preferred: formData.tuneData.is_preferred })
      };
    } else {
      // tuneData exists but is empty - explicitly clear the tune
      apiPayload.tune_id = null;
    }
  } else if (formData.tune_id) {
    // Fallback to tune_id if no tuneData but tune_id exists
    apiPayload.tune_id = Number(formData.tune_id);
  } else {
    // No tune data at all - explicitly set to null to clear any existing tune
    apiPayload.tune_id = null;
  }

  // Handle alternate tunes - separate existing vs new tunes
  console.log('[songEditService] Processing alternate tunes:', {
    alternateTunes: formData.alternateTunes,
    alternateTunesLength: formData.alternateTunes?.length
  });

  if (formData.alternateTunes && formData.alternateTunes.length > 0) {
    const existingTuneIds: number[] = [];
    const newTunes: any[] = [];

    formData.alternateTunes.forEach(tune => {
      if (tune.tune_id) {
        // Existing tune - use ID
        existingTuneIds.push(tune.tune_id);
      } else {
        // New tune - send details for creation
        newTunes.push({
          tune_name: tune.tune_name,
          ...(tune.meter_id && { meter_id: tune.meter_id }),
          ...(tune.is_preferred !== undefined && { is_preferred: tune.is_preferred })
        });
      }
    });

    console.log('[songEditService] Existing tune IDs:', existingTuneIds);
    console.log('[songEditService] New tunes to create:', newTunes);

    // Send existing tune IDs
    if (existingTuneIds.length > 0) {
      apiPayload.tune_ids = existingTuneIds;
    }

    // Send new tunes for creation
    if (newTunes.length > 0) {
      apiPayload.alternate_tunes = newTunes;
    }
  } else {
    // No alternate tunes
    apiPayload.tune_ids = [];
  }

  // Handle pending versions for new songs
  if (!isUpdate && formData.pendingVersions && formData.pendingVersions.length > 0) {
    console.log('[songEditService] Adding pending versions to payload:', formData.pendingVersions.length);

    // Strip tempId and prepare versions for API
    apiPayload.versions = formData.pendingVersions.map(v => ({
      version_title: v.version_title,
      alt_title: v.alt_title || null,
      arranger_name: v.arranger_name || null,
      arrangement_type: v.arrangement_type || null,
      performance_type: v.performance_type || null,
      key_signature: v.key_signature || null,
      verses_included: v.verses_included || null,
      video_link: v.video_link ? ensureValidUrl(v.video_link) : null,
      lyrics: v.lyrics || null,
      is_default: v.is_default || false
    }));
  }

  // Log the final payload
  console.log('[songEditService] Final API payload:', JSON.stringify(apiPayload, null, 2));

  try {
    const endpoint = isUpdate
      ? `/songs/update/${formData.song_id}`
      : '/songs';

    const method = isUpdate ? 'PUT' : 'POST';
    
    console.log(`[songEditService] Making ${method} request to ${endpoint}`);

    const response = await apiClient({
      method,
      url: endpoint,
      data: apiPayload
    });

    console.log('[songEditService] Save response:', response.data);
    return response.data;
  } catch (error) {
    console.error('[songEditService] Error saving song:', error);
    throw error;
  }
},

  deleteSong: async (songId: number): Promise<void> => {
    try {
      console.log('[songEditService] Deleting song:', songId);
      await apiClient.delete(`/songs/${songId}`);
      console.log('[songEditService] Song deleted successfully');
    } catch (error) {
      console.error('[songEditService] Error deleting song:', error);
      throw error;
    }
  }
};